import React, { useEffect, useContext, useRef, useState } from 'react';
import { Observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import FadeIn from 'react-fade-in';
import { toast } from 'react-toastify';
import { CheckBox } from 'devextreme-react/check-box';
import DataGrid, { Column, Grouping, GroupPanel, Selection } from 'devextreme-react/data-grid';
import { GlobalHotKeys } from 'react-hotkeys';

import BodyEnd from '../../_shared/BodyEnd';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import HtmlEditor from '../../_shared/HtmlEditor';
import ConfirmModal from '../../_shared/ConfirmModalComponent';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';
import ReferralUserExamDatasetGroupCreateStore from '../../../../stores/ReferralUserExamDatasetGroupUpdateStore';

import * as ErrorMessages from '../../../../constants/errorMessages';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';

import api from '../../../../api';

import './UpdateReferralUserExamDataGroup.scss';

function UpdateReferralUserExamDataGroup(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const dataSetRef = useRef(null);
    const allDatasets = useRef([]);
    const currentDatasets = useRef([]);
    const confirmModalRef = useRef(null);
    const quickDrawer = useContext(QuickDrawerStore);
    const referralExamDatasetGroup = useContext(ReferralUserExamDatasetGroupCreateStore);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        if (isMounted.current) {
            if (props && props.extraProps) {
                const { examPublishedTemplateId, examDatasetIds, selectedDatasets } = props.extraProps;
                const ExamDatasetIds = toJS(examDatasetIds);
                const SelectedDatasets = toJS(selectedDatasets);

                currentDatasets.current = SelectedDatasets;

                api.ExamDatasetGroups.search({
                    parameters: [{
                        field: 'ExamPublishedTemplateId',
                        value: examPublishedTemplateId,
                    }],
                    includeTotalCount: false,
                    loadProperties: true,
                    limit: 1
                })
                    .then(({ data }) => {
                        if (isMounted.current) {
                            const datasets = data && data.result && data.result.length > 0 ? data.result[0].examDatasets : [];
                            if (ExamDatasetIds && ExamDatasetIds.length > 0) {
                                allDatasets.current = ExamDatasetIds.map(di => { return datasets.filter(da => di === da.id)[0] }).filter(di => !!di);
                            }
                            else {
                                allDatasets.current = datasets;
                            }
                        }
                    })
                    .finally(() => {
                        if (isMounted.current) {
                            setIsReady(true);
                        }
                    })
            }
        }
        return () => {
            isMounted.current = false;
        }
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (referralExamDatasetGroup.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleNameChange = event => {
        const { value } = event.target;
        referralExamDatasetGroup.data.name = value;
        referralExamDatasetGroup.hasUnsavedChanges = true;
    }

    const handleSubjectChange = event => {
        const { value } = event.target;
        referralExamDatasetGroup.data.subject = value;
        referralExamDatasetGroup.hasUnsavedChanges = true;
    }

    const handleBodyChange = (e) => {
        referralExamDatasetGroup.data.bodyHtml = e.value;
        referralExamDatasetGroup.hasUnsavedChanges = true;
    }

    const handleSubmit = event => {
        event.preventDefault();

        if (fn.validateForm(validateRef.current)) {
            const selectedKeys = dataSetRef.current.instance.getSelectedRowKeys();
            referralExamDatasetGroup.data.examDatasetIds = selectedKeys;
            referralExamDatasetGroup.save()
                .then((data) => {
                    if (isMounted.current) {
                        if (fn.isFunction(props.onSuccess)) {
                            props.onSuccess(event, { updated: true, data: data });
                        }
                    }
                })
                .catch((error) => {
                    toast.error(() => <p>{error.response.data}</p>, { position: 'top-center' });
                })
        }
    }

    const handleDelete = event => {
        if (isMounted.current) {
            confirmModalRef.current.show({
                icon: <i className='fal fa-database text-danger mr-2'></i>,
                message: <>Continue to&nbsp;<span className='fw-500'>delete this referral template</span>? </>,
                option1ClassName: 'btn btn-danger shadow-0 bootbox-accept',
                option1Text: 'Delete',
                cancelText: 'Cancel',
                onOption1Click: handleConfirmDelete,
                onCancel: handleConfirmDeleteCancel
            })
        }
    }

    const handleConfirmDelete = event => {
        referralExamDatasetGroup.delete()
            .then(() => {
                if (isMounted.current) {
                    confirmModalRef.current.close();
                    if (fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, { updated: true, deleted: true });
                    }
                }
            })
    }

    const handleConfirmDeleteCancel = event => {
        if (isMounted.current) {
            confirmModalRef.current.close();
        }
    }

    const handleSelectAllChange = (element) => {
        if (element.value === true) {
            const keys = allDatasets.current.map(d => { return d.id });
            dataSetRef.current.instance.selectRows(keys);
        }
        else {
            dataSetRef.current.instance.selectRows([]);
        }
    }

    const handleCellClick = (e) => {
        if (e.rowType === 'group' && e.columnIndex !== 0) {
            const key = e.component.getKeyByRowIndex(e.rowIndex);
            const expanded = e.component.isRowExpanded(key);

            if (expanded) {
                e.component.collapseRow(key);
            }
            else {
                e.component.expandRow(key);
            }
        }
        else if (e.rowType === 'data' && e.columnIndex !== 0) {
            let selectedKeys = currentDatasets.current && currentDatasets.current.length > 0 ? currentDatasets.current.map(d => { return d.id }) : [];

            if (selectedKeys.some(k => k === e.data.id)) {
                selectedKeys = selectedKeys.filter(k => k !== e.data.id);
            }
            else {
                selectedKeys = [...selectedKeys, ...[e.data.id]];
            }

            dataSetRef.current.instance.selectRows(selectedKeys);
            currentDatasets.current = allDatasets.current.filter((d) => selectedKeys.some(id => d.id === id));
        }
    }

    const handleCellPrepared = (cellInfo) => {
        if (cellInfo.rowType === 'group') {
            cellInfo.cellElement.classList.add('cursor-pointer');
        }
    }

    const handleSelectedRowKeysChange = () => {
        const categories = allDatasets.current.map(d => { return d.category }).filter((v, i, a) => a.indexOf(v) === i);

        if (categories && categories.length > 0) {
            for (let ci = 0; ci < categories.length; ci++) {
                const groupElement = document.querySelector(`[data-category="${categories[ci]}"]`);
                if (groupElement) {
                    groupElement.innerHTML = getSelectedCount(categories[ci]);
                }
            }
        }

        referralExamDatasetGroup.hasUnsavedChanges = true;
    }

    const handleContentReady = ({ element }) => {
        const cols = element.querySelectorAll('.dx-datagrid colgroup col');
        const groupRows = element.querySelectorAll('.dx-datagrid .dx-group-row');
        const dataRows = element.querySelectorAll('.dx-datagrid .dx-data-row');

        for (let gi = 0; gi < groupRows.length; gi++) {
            groupRows[gi].deleteCell(0);
        }

        for (let di = 0; di < dataRows.length; di++) {
            dataRows[di].deleteCell(1);
        }

        cols[0].style.width = '30px';
        cols[1].remove();

        if (currentDatasets.current && currentDatasets.current.length > 0) {
            const keys = currentDatasets.current.map(d => { return d.id });
            dataSetRef.current.instance.selectRows(keys);
        }
    }

    const renderSelectedCounts = (group) => {
        return <small>(<span data-category={`${group.key}`}>{getSelectedCount(group.key)}</span>{` / ${allDatasets.current.filter(d => d.category === group.key).length}`})</small>
    }

    const getSelectedCount = (category) => {
        if (dataSetRef.current && dataSetRef.current.instance) {
            const selectedKeys = dataSetRef.current.instance.getSelectedRowKeys();
            const selectedCategoryKeys = allDatasets.current.filter(d => d.category === category && selectedKeys.some(s => s === d.id));

            if (selectedCategoryKeys && selectedCategoryKeys.length > 0) {
                return selectedCategoryKeys.length;
            }
        }

        return 0;
    }

    return <>
        <Observer>{() =>
            <>
                {
                    (props.drawer === quickDrawer.drawerOpened) ?
                        <GlobalHotKeys
                            keyMap={{
                                close: ['esc'],
                            }}
                            handlers={{
                                close: event => {
                                    handleCancel(event)
                                },
                            }}
                            allowChanges={true}
                        /> : null
                }
            </>
        }</Observer>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <fieldset>
                <div className='update-referral-exam-data-group-container quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('exam-dataset', 'default')}
                        action={referralExamDatasetGroup.id ? 'Update' : 'New'}
                        category='Referral Letter Template'
                        className='exam'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <Observer>{() =>
                            <>
                                {
                                    isReady ?
                                        <FadeIn>
                                            <div className='body-content'>
                                                <section className='mb-2'>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-2 validate validate-required'>
                                                                <label className='required'><small>Name</small></label>
                                                                <Observer>{() =>
                                                                    <input
                                                                        type='text'
                                                                        className='form-control'
                                                                        value={referralExamDatasetGroup.data.name ? referralExamDatasetGroup.data.name : ''}
                                                                        onChange={handleNameChange}
                                                                    />
                                                                }</Observer>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-2'>
                                                                <label><small>Reason for referral</small></label>
                                                                <Observer>{() =>
                                                                    <input
                                                                        type='text'
                                                                        className='form-control'
                                                                        value={referralExamDatasetGroup.data.subject ? referralExamDatasetGroup.data.subject : ''}
                                                                        onChange={handleSubjectChange}
                                                                    />
                                                                }</Observer>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-2'>
                                                                <label><small>Body</small></label>
                                                                <Observer>{() =>
                                                                    <HtmlEditor
                                                                        height='150px'
                                                                        defaultValue={referralExamDatasetGroup.data.bodyHtml}
                                                                        onValueChanged={handleBodyChange}
                                                                    >
                                                                    </HtmlEditor>
                                                                }</Observer>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                                {
                                                    allDatasets.current && allDatasets.current.length > 0 ?
                                                        <section
                                                            className='m-0'
                                                        >
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-2'>
                                                                        <CheckBox
                                                                            defaultValue={false}
                                                                            text='Select all'
                                                                            onValueChanged={handleSelectAllChange}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    {
                                                                        allDatasets.current.length > 0 ?
                                                                            <DataGrid
                                                                                ref={dataSetRef}
                                                                                keyExpr={'id'}
                                                                                dataSource={allDatasets.current}
                                                                                activeStateEnabled={false}
                                                                                focusStateEnabled={false}
                                                                                hoverStateEnabled={false}
                                                                                showBorders={true}
                                                                                showRowLines={true}
                                                                                showColumnHeaders={false}
                                                                                allowColumnReordering={false}
                                                                                allowColumnResizing={false}
                                                                                sorting={{ mode: 'none' }}
                                                                                paging={{ enabled: false }}
                                                                                onCellClick={handleCellClick}
                                                                                onCellPrepared={handleCellPrepared}
                                                                                onSelectedRowKeysChange={handleSelectedRowKeysChange}
                                                                                onContentReady={handleContentReady}
                                                                            >
                                                                                <GroupPanel visible={true} />
                                                                                <Grouping autoExpandAll={false} />
                                                                                <Selection
                                                                                    mode='multiple'
                                                                                    selectAllMode='allPages'
                                                                                    showCheckBoxesMode='always'
                                                                                />
                                                                                <Column
                                                                                    dataField={'category'}
                                                                                    dataType='string'
                                                                                    groupIndex={0}
                                                                                    groupCellRender={({ data }) => {
                                                                                        return <div className='d-flex'>
                                                                                            <div className='flex-1'>{data.key}</div>
                                                                                            <div className='px-3'>{renderSelectedCounts(data)}</div>
                                                                                        </div>
                                                                                    }} />
                                                                                <Column
                                                                                    dataField={'label'}
                                                                                    alignment='left'
                                                                                    width={'100%'}
                                                                                    activeStateEnabled={false}
                                                                                    focusStateEnabled={false}
                                                                                    hoverStateEnabled={false}
                                                                                />
                                                                            </DataGrid> : null
                                                                    }
                                                                </div>
                                                            </div>
                                                        </section> : null
                                                }
                                            </div>
                                        </FadeIn> :
                                        <FadeIn>
                                            <div className='body-content'>
                                                <section>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='text-muted'>No exam dataset found.</div>
                                                        </div>
                                                    </div>
                                                </section>
                                            </div>
                                        </FadeIn>
                                }
                            </>
                        }</Observer>
                    </div>
                    <div className='quick-drawer-action pl-3'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='float-right'>
                                    {
                                        referralExamDatasetGroup.id ?
                                            <button
                                                type='button'
                                                className='btn btn-danger mr-2'
                                                onClick={handleDelete}
                                            >Delete</button> :
                                            <button
                                                type='button'
                                                className='btn btn-link btn-cancel mr-2'
                                                onClick={handleCancel}
                                            >Cancel</button>
                                    }
                                    <Observer>{() =>
                                        <button
                                            type='submit'
                                            className='btn btn-success'
                                        >Save</button>
                                    }</Observer>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
        <BodyEnd>
            <ConfirmModal ref={confirmModalRef} />
        </BodyEnd>
    </>
}

export default UpdateReferralUserExamDataGroup;